import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Color } from '@tiptap/extension-color';
import { FontSize, TextStyle } from '@tiptap/extension-text-style';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';

import { Box, Button, ButtonGroup, Chip, Grid, IconButton, MenuItem, Select, Tooltip, Typography } from '@mui/material';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        fontSize: {
            setFontSize: (size: string) => ReturnType;
            unsetFontSize: () => ReturnType;
        };
    }
}

interface VariableOption {
    code: string;
    description: string;
}

interface MyRichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    variables: VariableOption[];
    onLocalImageAdded: (file: File, localUrl: string) => void;
}

const MyRichTextEditor: React.FC<MyRichTextEditorProps> = ({ value, onChange, variables, onLocalImageAdded }) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            TextStyle,
            Color,
            FontSize,
            Image.configure({ allowBase64: false }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    if (!editor) return null;

    const handleLocalImageSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        const localBlobUrl = URL.createObjectURL(file);
        onLocalImageAdded(file, localBlobUrl);

        editor.chain().focus().setImage({ src: localBlobUrl }).run();
    };

    const currentFontSize = editor.getAttributes('textStyle').fontSize || '16px';

    return (
        <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
            
            <Box sx={{ p: 1, borderBottom: '1px solid #ccc', backgroundColor: '#f5f5f5', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                
                <Select
                    value={currentFontSize}
                    size="small"
                    sx={{ height: '34px', backgroundColor: '#fff', width: '140px', fontSize: '0.85rem' }}
                    onChange={(e) => {
                        editor.chain().focus().setFontSize(e.target.value as string).run();
                    }}
                >
                    <MenuItem value="12px">Pequeño (12px)</MenuItem>
                    <MenuItem value="16px">Normal (16px)</MenuItem>
                    <MenuItem value="20px">Mediano (20px)</MenuItem>
                    <MenuItem value="24px">Grande (24px)</MenuItem>
                    <MenuItem value="32px">Título (32px)</MenuItem>
                </Select>

                <ButtonGroup size="small" variant="text" sx={{ height: '34px', backgroundColor: '#fff' }}>
                    <IconButton onClick={() => editor.chain().focus().toggleBold().run()} color={editor.isActive('bold') ? 'primary' : 'default'}>
                        <FormatBoldIcon />
                    </IconButton>
                    <IconButton onClick={() => editor.chain().focus().toggleItalic().run()} color={editor.isActive('italic') ? 'primary' : 'default'}>
                        <FormatItalicIcon />
                    </IconButton>
                    <IconButton onClick={() => editor.chain().focus().toggleStrike().run()} color={editor.isActive('strike') ? 'primary' : 'default'}>
                        <FormatStrikethroughIcon />
                    </IconButton>
                </ButtonGroup>

                <ButtonGroup size="small" variant="text" sx={{ height: '34px', backgroundColor: '#fff' }}>
                    <IconButton onClick={() => editor.chain().focus().setTextAlign('left').run()} color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}>
                        <FormatAlignLeftIcon />
                    </IconButton>
                    <IconButton onClick={() => editor.chain().focus().setTextAlign('center').run()} color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}>
                        <FormatAlignCenterIcon />
                    </IconButton>
                    <IconButton onClick={() => editor.chain().focus().setTextAlign('right').run()} color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}>
                        <FormatAlignRightIcon />
                    </IconButton>
                </ButtonGroup>

                <Box sx={{ display: 'flex', alignItems: 'center', height: '34px' }}>
                    <input 
                        type="color" 
                        value={editor.getAttributes('textStyle').color || '#000000'}
                        onInput={e => editor.chain().focus().setColor((e.target as HTMLInputElement).value).run()} 
                        style={{ width: '32px', height: '32px', border: '1px solid #ccc', borderRadius: '4px', padding: 0, cursor: 'pointer', backgroundColor: '#fff' }}
                        title="Color del texto"
                    />
                </Box>

                <Button variant="outlined" component="label" size="small" startIcon={<ImageOutlinedIcon />} sx={{ ml: 'auto', height: '34px', backgroundColor: '#fff' }}>
                    Añadir Imagen
                    <input type="file" hidden accept="image/*" onChange={handleLocalImageSelection} />
                </Button>
            </Box>

            <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                <Typography variant="caption" fontWeight="bold" display="block" color="text.secondary" mb={1}>
                    Variables disponibles (Haz clic para insertar):
                </Typography>
                <Grid container gap={1}>
                    {variables.map((v) => (
                        <Tooltip key={v.code} title={v.description} arrow placement="top">
                            <Chip 
                                label={v.code} 
                                onClick={() => editor.chain().focus().insertContent(` ${v.code} `).run()}
                                color="secondary"
                                variant="outlined"
                                size="small"
                                sx={{ cursor: 'pointer', fontWeight: '600' }}
                            />
                        </Tooltip>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ 
                p: 2, minHeight: '280px', maxHeight: '400px', overflowY: 'auto',
                '& .ProseMirror': { outline: 'none', minHeight: '250px' },
                '& .ProseMirror img': { maxWidth: '100%', height: 'auto', borderRadius: '4px', display: 'block', margin: '10px 0' }
            }}>
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
};

export default MyRichTextEditor;